import { formatAmount } from "../../utils/FormatPerson";
import { formatOrdinalActivityDate } from "./userActivityDateFormat";

export const USER_ACTIVITY_TX_PAGE_SIZE = 50;

export type UserActivityTransaction = {
  _id: string;
  userId?: string;
  reference?: string;
  type?: string;
  _source?: string;
  amount?: number;
  fee?: number;
  fees?: number;
  narration?: string;
  metadata?: Record<string, unknown>;
  status?: string;
  createdAt?: string;
  processedAt?: string;
  updatedAt?: string;
  currency?: string;
  asset?: string | TransactionAsset;
  usdtAmount?: number;
  amountNGN?: number;
  amountUSDT?: number;
  txid?: string;
  source?: string;
  payoutAmount?: number;
  isAutomated?: boolean;
  batchId?: string;
  recipient?: TransactionRecipient;
  transaction?: TransactionLinkedRef;
  logs?: TransactionPayoutLog[];
  rates?: TransactionRate[];
  utility?: { type?: string; data?: Record<string, unknown> | null };
  from?: { asset?: string; amount?: number };
  to?: { asset?: string; amount?: number };
  counterParty?: {
    accountName?: string;
    accountNumber?: string;
    bankName?: string;
  };
  withdrawalId?: string;
  toDeexUser?: string;
  toAddress?: string;
  network?: string;
  isPending?: boolean;
  balanceDeducted?: boolean;
  anchorPaymentId?: string;
  idempotencyKey?: string;
  provider?: string | { name?: string; data?: unknown };
  ledger?: UserTransactionLedger;
  [key: string]: unknown;
};

export type TransactionAsset = {
  name?: string;
  amount?: number;
  amountInUSD?: number;
};

export type TransactionRecipient = {
  accountName?: string;
  accountNumber?: string;
  bankCode?: string;
  bankName?: string;
};

export type TransactionLinkedRef = {
  type?: string;
  id?: string;
};

export type TransactionPayoutLog = {
  time?: string;
  comment?: string;
  _id?: string;
};

export type TransactionRate = {
  name?: string;
  abbr?: string;
  rate?: number;
};

export type UserTransactionLedger = {
  _id?: string;
  userId?: string;
  transaction?: {
    type?: string;
    asset?: string;
    amount?: number;
    transactionId?: string;
    fee?: number;
    provider?: string;
    _id?: string;
  };
  balancesBefore?: Record<string, unknown>;
  balancesAfter?: Record<string, unknown>;
  timestamp?: string;
  type?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type UserTransactionsPageMeta = {
  page?: number;
  limit?: number;
  transactionsCount?: number;
  totalPages?: number;
};

export type UserTransactionsPageResponse = {
  transactions: UserActivityTransaction[];
  meta: UserTransactionsPageMeta;
};

export type UserActivityTxStatusTone = "success" | "pending" | "failed";

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function num(v: unknown): number | undefined {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function titleCaseWords(value: string): string {
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function transactionAssetObject(
  tx: UserActivityTransaction
): TransactionAsset | null {
  const asset = tx.asset;
  if (asset && typeof asset === "object" && !Array.isArray(asset)) {
    return asset as TransactionAsset;
  }
  return null;
}

export function transactionRecipient(
  tx: UserActivityTransaction
): TransactionRecipient | null {
  if (tx.recipient && typeof tx.recipient === "object") {
    return tx.recipient;
  }
  if (tx.counterParty && typeof tx.counterParty === "object") {
    return tx.counterParty;
  }
  return null;
}

export function transactionProviderData(
  tx: UserActivityTransaction
): Record<string, unknown> | null {
  const provider = tx.provider;
  if (!provider || typeof provider !== "object" || Array.isArray(provider)) {
    return null;
  }
  const data = (provider as { data?: unknown }).data;
  if (data != null && typeof data === "object" && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return null;
}

export function getTransactionProviderLabel(
  tx: UserActivityTransaction
): string {
  const provider = tx.provider;
  if (typeof provider === "string" && provider.trim()) return provider.trim();
  if (provider && typeof provider === "object" && "name" in provider) {
    return str((provider as { name?: string }).name);
  }
  return "";
}

export function formatTransactionRates(
  rates: TransactionRate[] | undefined
): string {
  if (!rates?.length) return "—";
  const usdt = rates.find((r) => str(r.abbr).toUpperCase() === "USDT");
  if (usdt?.rate != null) {
    return `₦${formatAmount(usdt.rate)} / USDT (${rates.length} assets)`;
  }
  const first = rates[0];
  if (first?.abbr && first.rate != null) {
    return `₦${formatAmount(first.rate)} / ${str(first.abbr).toUpperCase()}`;
  }
  return `${rates.length} rate${rates.length === 1 ? "" : "s"}`;
}

/** Naira ledger amounts from the API are in kobo. */
export function formatNairaFromKobo(kobo: number | undefined): string {
  if (kobo == null || Number.isNaN(kobo)) return "—";
  return `₦ ${formatAmount(kobo / 100)}`;
}

export function utilityData(
  tx: UserActivityTransaction
): Record<string, unknown> | null {
  const u = tx.utility;
  if (!u || typeof u !== "object") return null;
  const d = (u as { data?: unknown }).data;
  if (d != null && typeof d === "object" && !Array.isArray(d)) {
    return d as Record<string, unknown>;
  }
  return null;
}

export function normalizeUserTransactionsResponse(
  body: unknown
): UserTransactionsPageResponse {
  if (!body || typeof body !== "object") {
    return { transactions: [], meta: {} };
  }
  const root = body as Record<string, unknown>;
  const rawTx = root.transactions;
  const transactions = Array.isArray(rawTx)
    ? (rawTx as UserActivityTransaction[])
    : [];

  const pagination = root.pagination;
  if (pagination && typeof pagination === "object" && !Array.isArray(pagination)) {
    const p = pagination as Record<string, unknown>;
    return {
      transactions,
      meta: {
        page: num(p.page) ?? 1,
        limit: num(p.limit) ?? USER_ACTIVITY_TX_PAGE_SIZE,
        transactionsCount: num(p.total) ?? transactions.length,
        totalPages: Math.max(1, num(p.pages) ?? 1),
      },
    };
  }

  const metaRaw = root.meta;
  const meta =
    metaRaw != null && typeof metaRaw === "object" && !Array.isArray(metaRaw)
      ? (metaRaw as UserTransactionsPageMeta)
      : {};
  return { transactions, meta };
}

export function getTransactionSourceLabel(tx: UserActivityTransaction): string {
  const source = getTransactionSourceKey(tx);
  switch (source) {
    case "naira":
      return "Naira";
    case "crypto-withdrawal":
      return "Crypto";
    case "crypto-swap":
      return "Swap";
    case "sell":
      return "Sell";
    case "sell-deposit":
      return "Sell deposit";
    case "payout":
      return "Payout";
    default:
      if (!source || source === "unknown") return "—";
      return titleCaseWords(source);
  }
}

export function getTransactionSourceKey(tx: UserActivityTransaction): string {
  const source = str(tx._source).toLowerCase().trim();
  if (source === "sell-deposits") return "sell-deposit";
  return source || "unknown";
}

export function getTransactionTypeLabel(tx: UserActivityTransaction): string {
  const source = str(tx._source).toLowerCase();
  const type = str(tx.type).toUpperCase();

  if (source === "sell") return "Sell";
  if (source === "sell-deposit") {
    const providerType = str(transactionProviderData(tx)?.type);
    return providerType ? titleCaseWords(providerType) : "Sell deposit";
  }
  if (source === "payout") {
    const payoutSource = str(tx.source);
    const linkedType = str(tx.transaction?.type);
    if (payoutSource && linkedType) {
      return `${titleCaseWords(payoutSource)} payout`;
    }
    if (payoutSource) return titleCaseWords(payoutSource);
    return "Payout";
  }
  if (source === "crypto-swap") return "Asset swap";
  if (source === "crypto-withdrawal") {
    const utilType = str(tx.utility?.type);
    if (tx.toDeexUser) return "Send to Deex user";
    if (tx.toAddress) return "On-chain withdrawal";
    if (utilType) return utilType.replace(/_/g, " ");
    return "Crypto withdrawal";
  }
  if (source === "naira") {
    switch (type) {
      case "BILL_PAYMENT":
        return "Bill payment";
      case "WITHDRAWAL":
        return "Withdrawal";
      case "DEPOSIT":
        return "Deposit";
      case "SEND-MONEY":
        return "Send money";
      default:
        return type ? type.replace(/_/g, " ") : "Naira";
    }
  }
  return type ? type.replace(/_/g, " ") : getTransactionSourceLabel(tx);
}

export function getTransactionAssetLabel(tx: UserActivityTransaction): string {
  if (tx.from?.asset) return str(tx.from.asset).toUpperCase();
  const assetObj = transactionAssetObject(tx);
  if (assetObj?.name) return str(assetObj.name).toUpperCase();
  if (tx.currency) return str(tx.currency).toUpperCase();
  if (typeof tx.asset === "string" && tx.asset) return tx.asset.toUpperCase();
  if (str(tx._source).toLowerCase() === "naira" || tx.type) return "NGN";
  return "—";
}

export function getTransactionNarration(tx: UserActivityTransaction): string {
  if (tx.narration) return tx.narration;

  const source = str(tx._source).toLowerCase();
  const recipient = transactionRecipient(tx);
  if (recipient?.accountNumber) {
    const bank = recipient.bankName ? ` · ${recipient.bankName}` : "";
    return `${recipient.accountName ?? "Account"} · ${recipient.accountNumber}${bank}`;
  }

  if (source === "sell-deposit") {
    const providerData = transactionProviderData(tx);
    const hash = str(providerData?.hash);
    const sourceAddresses = providerData?.sourceAddresses;
    if (Array.isArray(sourceAddresses) && sourceAddresses.length > 0) {
      return `From ${String(sourceAddresses[0])}`;
    }
    if (hash) return `On-chain deposit · ${hash}`;
    if (tx.toAddress) return `To ${tx.toAddress}`;
  }

  if (source === "payout") {
    const payoutSource = str(tx.source);
    if (payoutSource) return titleCaseWords(payoutSource);
  }

  const meta = tx.metadata;
  if (meta && typeof meta === "object") {
    const billType = str(meta.billType);
    const phone = str(meta.phone);
    const meter = str(meta.meterNumber);
    if (billType && phone) return `${billType} · ${phone}`;
    if (billType && meter) return `${billType} · ${meter}`;
    if (billType) return billType;
  }
  const util = utilityData(tx);
  if (util) {
    const phone = str(util.phone);
    const meter = str(util.meter_number);
    if (phone) return `Phone: ${phone}`;
    if (meter) return `Meter: ${meter}`;
  }
  if (tx.toDeexUser) return "Transfer to Deex user";
  if (tx.toAddress) return `To ${tx.toAddress}`;
  if (tx.counterParty?.accountNumber) {
    return `${tx.counterParty.accountName ?? "Account"} · ${tx.counterParty.accountNumber}`;
  }
  return "—";
}

export function resolveUserTransactionStatus(tx: UserActivityTransaction): {
  label: string;
  tone: UserActivityTxStatusTone;
} {
  if (tx.isPending === true) {
    return { label: "PENDING", tone: "pending" };
  }

  const top = str(tx.status).toUpperCase();
  const util = utilityData(tx);
  const uTxn = str(util?.transaction_status).toLowerCase();
  const uAlt = str(util?.status).toLowerCase();

  if (top === "COMPLETED" || top === "SUCCESS") {
    return { label: "COMPLETED", tone: "success" };
  }
  if (top === "FAILED" || top === "FAILURE") {
    return { label: "FAILED", tone: "failed" };
  }
  if (top === "HOLD") {
    return { label: "HOLD", tone: "pending" };
  }
  if (top === "PENDING" || top === "PROCESSING") {
    return { label: top, tone: "pending" };
  }
  if (
    uTxn === "success" ||
    uTxn === "successful" ||
    uAlt === "success" ||
    uAlt === "successful"
  ) {
    return { label: "COMPLETED", tone: "success" };
  }
  if (uTxn === "failed" || uAlt === "failed") {
    return { label: "FAILED", tone: "failed" };
  }
  if (uTxn === "processing" || uTxn === "pending") {
    return { label: "PROCESSING", tone: "pending" };
  }

  return {
    label: top || "UNKNOWN",
    tone: top === "UNKNOWN" ? "pending" : "pending",
  };
}

export function getUserTransactionStatusLabel(
  tx: UserActivityTransaction
): string {
  return resolveUserTransactionStatus(tx).label;
}

export function canMarkPayoutAsPaid(tx: UserActivityTransaction): boolean {
  if (getTransactionSourceKey(tx) !== "payout") return false;
  const { label } = resolveUserTransactionStatus(tx);
  return label === "FAILED" || label === "PENDING" || label === "PROCESSING";
}

export function getUserTransactionAmountPlainText(
  tx: UserActivityTransaction
): string {
  const source = str(tx._source).toLowerCase();
  const assetObj = transactionAssetObject(tx);

  if (source === "crypto-swap" && tx.from) {
    const fromAmt = num(tx.from.amount);
    const toAmt = num(tx.to?.amount);
    const fromAsset = str(tx.from.asset);
    const toAsset = str(tx.to?.asset);
    return `${fromAmt != null ? formatAmount(fromAmt) : "—"} ${fromAsset}${
      toAsset ? ` → ${toAmt != null ? formatAmount(toAmt) : "—"} ${toAsset}` : ""
    }`;
  }

  if (source === "payout") {
    const cryptoAmt = num(assetObj?.amount);
    const assetName = str(assetObj?.name).toUpperCase();
    const payoutNgn = num(tx.payoutAmount);
    if (cryptoAmt != null && assetName) {
      let line = `${formatAmount(cryptoAmt)} ${assetName}`;
      if (payoutNgn != null && payoutNgn > 0) {
        line += ` · ₦${formatAmount(payoutNgn)}`;
      }
      return line;
    }
    if (payoutNgn != null) return `₦ ${formatAmount(payoutNgn)}`;
  }

  if (source === "naira" || (!tx.currency && !assetObj && !tx.from && tx.amount != null)) {
    const feeKobo = num(tx.fee) ?? num(tx.fees) ?? 0;
    const amtKobo = num(tx.amount);
    const base = amtKobo != null ? formatNairaFromKobo(amtKobo) : "—";
    return feeKobo > 0
      ? `${base} (fee ${formatNairaFromKobo(feeKobo)})`
      : base;
  }

  if (tx.currency && tx.amount != null) {
    const cur = str(tx.currency).toUpperCase();
    let line = `${formatAmount(tx.amount)} ${cur}`;
    const ngn = num(tx.amountNGN);
    if (ngn != null && ngn > 0) line += ` · ₦${formatAmount(ngn)}`;
    const usdt = num(tx.usdtAmount);
    if (usdt != null && cur !== "USDT") line += ` · ${formatAmount(usdt)} USDT`;
    const fee = num(tx.fee) ?? num(tx.fees);
    if (fee != null && fee > 0) line += ` (fee ${formatAmount(fee)} ${cur})`;
    return line;
  }

  if (assetObj?.amount != null && assetObj.name) {
    const usdt = num(tx.amountUSDT) ?? num(assetObj.amountInUSD);
    const base = `${formatAmount(assetObj.amount)} ${str(assetObj.name).toUpperCase()}`;
    return usdt != null ? `${base} · ${formatAmount(usdt)} USDT` : base;
  }

  if (typeof tx.asset === "string" && tx.asset && tx.amount != null) {
    const usdt = num(tx.amountUSDT);
    const base = `${formatAmount(tx.amount)} ${str(tx.asset).toUpperCase()}`;
    return usdt != null ? `${base} · ${formatAmount(usdt)} USDT` : base;
  }

  const amt = num(tx.amount);
  return amt != null ? formatAmount(amt) : "—";
}

export function sortAmountValue(tx: UserActivityTransaction): number {
  if (tx.from?.amount != null) return tx.from.amount;
  const source = str(tx._source).toLowerCase();
  const assetObj = transactionAssetObject(tx);
  if (source === "payout") {
    const payoutNgn = num(tx.payoutAmount);
    if (payoutNgn != null && payoutNgn > 0) return payoutNgn;
    if (assetObj?.amount != null) return assetObj.amount;
  }
  if (source === "naira" && tx.amount != null) return tx.amount / 100;
  if (tx.amountNGN != null && tx.amountNGN > 0) return tx.amountNGN;
  if (assetObj?.amount != null) return assetObj.amount;
  if (tx.amount != null) return tx.amount;
  if (tx.usdtAmount != null) return tx.usdtAmount;
  return 0;
}

export function getTransactionDateIso(tx: UserActivityTransaction): string {
  return str(tx.createdAt) || str(tx.processedAt) || str(tx.updatedAt);
}

export function formatTransactionDate(tx: UserActivityTransaction): string {
  return formatOrdinalActivityDate(getTransactionDateIso(tx));
}

export function statusBadgeClass(tone: UserActivityTxStatusTone): string {
  if (tone === "success") {
    return "inline-flex items-center justify-center rounded-full bg-[#013019] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#03C668]";
  }
  if (tone === "failed") {
    return "inline-flex items-center justify-center rounded-full bg-[#3d2020] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#e37979]";
  }
  return "inline-flex items-center justify-center rounded-full bg-[#2a2410] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#CB9200]";
}

export function sourceBadgeClass(source: string): string {
  const s = source.toLowerCase().replace(/\s+/g, "-");
  if (s === "naira") {
    return "border border-[#0B75C2]/30 bg-[#0A2A45]/90 text-[#60B7F6]";
  }
  if (s === "crypto" || s === "crypto-withdrawal") {
    return "border border-[#4FB0FF]/25 bg-[#1a2433]/90 text-[#BEE1FB]";
  }
  if (s === "swap" || s === "crypto-swap") {
    return "border border-[#9747FF]/25 bg-[#2a1f45]/90 text-[#C9A0FF]";
  }
  if (s === "sell") {
    return "border border-[#22C55E]/25 bg-[#1f2d1a]/90 text-[#8FD99A]";
  }
  if (s === "sell-deposit" || s === "sell-deposits") {
    return "border border-[#F28A0F]/35 bg-[#2d2210]/90 text-[#F5B84A]";
  }
  if (s === "payout") {
    return "border border-[#EAB308]/30 bg-[#2a2410]/90 text-[#F5D565]";
  }
  return "border border-[#2C363E] bg-[#1C2227] text-[#869AA9]";
}

function ledgerAssetKey(key: string): string {
  return key.toUpperCase();
}

/** Parses ledger balance maps for compact display (null = cleared/zero). */
function ledgerBalanceDisplayEntries(
  balances: Record<string, unknown> | undefined
): Array<{ asset: string; amount: number | null }> {
  if (!balances) return [];

  const map = new Map<string, number | null>();
  for (const [key, value] of Object.entries(balances)) {
    const asset = ledgerAssetKey(key);
    if (value == null) {
      map.set(asset, null);
      continue;
    }
    if (typeof value === "number" && !Number.isNaN(value)) {
      map.set(asset, value);
    }
  }

  return Array.from(map.entries())
    .map(([asset, amount]) => ({ asset, amount }))
    .sort((a, b) => a.asset.localeCompare(b.asset));
}

/** Parses ledger balance maps, skipping null/invalid values. */
export function parseLedgerBalances(
  balances: Record<string, unknown> | undefined
): Array<{ asset: string; amount: number }> {
  if (!balances) return [];

  const map = new Map<string, number>();
  for (const [key, value] of Object.entries(balances)) {
    if (typeof value !== "number" || Number.isNaN(value)) continue;
    map.set(ledgerAssetKey(key), value);
  }

  return Array.from(map.entries())
    .map(([asset, amount]) => ({ asset, amount }))
    .sort((a, b) => a.asset.localeCompare(b.asset));
}

export type LedgerBalanceRow = {
  asset: string;
  before: number | null;
  after: number | null;
};

/** Merges before/after ledger balances per asset for display. */
export function getLedgerBalanceRows(
  ledger: UserTransactionLedger | undefined
): LedgerBalanceRow[] {
  if (!ledger) return [];

  const assets = new Set<string>();
  const before = new Map<string, number | null>();
  const after = new Map<string, number | null>();

  const ingest = (
    balances: Record<string, unknown> | undefined,
    target: Map<string, number | null>
  ) => {
    if (!balances) return;
    for (const [key, value] of Object.entries(balances)) {
      const asset = ledgerAssetKey(key);
      assets.add(asset);
      if (value == null) {
        target.set(asset, null);
        continue;
      }
      if (typeof value === "number" && !Number.isNaN(value)) {
        target.set(asset, value);
      }
    }
  };

  ingest(ledger.balancesBefore, before);
  ingest(ledger.balancesAfter, after);

  const primary = ledger.transaction?.asset
    ? ledgerAssetKey(ledger.transaction.asset)
    : undefined;

  return Array.from(assets)
    .sort((a, b) => {
      if (primary) {
        if (a === primary) return -1;
        if (b === primary) return 1;
      }
      return a.localeCompare(b);
    })
    .map((asset) => ({
      asset,
      before: before.has(asset) ? (before.get(asset) ?? null) : null,
      after: after.has(asset) ? (after.get(asset) ?? null) : null,
    }));
}

export function formatLedgerBalanceAmount(
  value: number | null | undefined
): string {
  if (value == null) return "0";
  return formatAmount(value);
}

export function formatLedgerBalancesCompact(
  balances: Record<string, unknown> | undefined,
  primaryAsset?: string
): string {
  const entries = ledgerBalanceDisplayEntries(balances);
  if (!entries.length) return "—";

  const sorted = primaryAsset
    ? [...entries].sort((a, b) => {
        const p = ledgerAssetKey(primaryAsset);
        if (a.asset === p) return -1;
        if (b.asset === p) return 1;
        return a.asset.localeCompare(b.asset);
      })
    : entries;

  return sorted
    .map(({ asset, amount }) =>
      amount == null ? `0 ${asset}` : `${formatAmount(amount)} ${asset}`
    )
    .join(" · ");
}

export function formatLedgerBalancesPlainText(
  ledger: UserTransactionLedger | undefined
): { before: string; after: string } {
  const primary = ledger?.transaction?.asset;
  return {
    before: formatLedgerBalancesCompact(ledger?.balancesBefore, primary),
    after: formatLedgerBalancesCompact(ledger?.balancesAfter, primary),
  };
}
